import { mapActions } from 'vuex';
import { CHATBOT_COMMANDS, VP_DID_NOT_RECEIVE_CHAT_OPTIONS, CUSTOMER_WAITING_STATE_CHAT_OPTIONS } from '~/utils/chat-flags';
import { getPhoneCreditPrepaidTransaction } from '~/api/phone-credit-prepaid';
import { getInvoices } from '~/api/invoices';
import { chatbotUnstuckTransaction, chatbotRefundTransaction } from '~/api/mitra-bantuan';
import { base64UrlToJson } from '~/utils/base64-utils';
import Tracker from '~/mixins/tracker';

export default {
  mixins: [Tracker],
  methods: {
    ...mapActions({
      showLoading: 'autorefund/showLoading',
      hideLoading: 'autorefund/hideLoading',
    }),

    /**
     * Unstuck the transaction to make sure get the latest data from the partner dashboard
     * Support phone credit and data plan
     * @command - [apicall] unstuckTrxAutorefundForProcessed [/apicall]
     * @param {object} currentTrx - currently selected transaction object
     */
    async unstuckTrxAutorefundForProcessed(currentTrx) {
      this.showLoading();
      if (!currentTrx) {
        this.$zopim.sendChatMsg(CHATBOT_COMMANDS.responses.noSelectedTransaction);
        this.hideLoading();
        return;
      }

      const responses = {
        unstuckTrxIsDoneAutorefundForProcessed: '[apiresponse] unstuckTrxIsDoneAutorefundForProcessed [/apiresponse]',
      };

      try {
        // Get invoice data
        const {
          data: [invoiceData],
        } = await getInvoices(this.$axios, { keywords: currentTrx.paymentId });
        const {
          transactions: [invoiceTrx],
        } = invoiceData;

        await chatbotUnstuckTransaction(this.$axios, {
          id: invoiceTrx.id,
          requestPayload: {
            type: invoiceTrx.type
          }
        });
      } catch (error) {
        console.error('[ERROR][Autorefund For Processed] Something is wrong when trying to unstuck transaction', error);
      } finally {
        this.$zopim.sendChatMsg(responses.unstuckTrxIsDoneAutorefundForProcessed);
        this.hideLoading();
      }
    },

    /**
     * Check user's transaction state
     * Support phone credit and data plan
     * @command - [apicall] checkTrxStateAutorefundForProcessed [/apicall]
     * @param {object} currentTrx - currently selected transaction object
     */
    async checkTrxStateAutorefundForProcessed(currentTrx = null) {
      this.showLoading();
      if (!currentTrx) {
        this.$zopim.sendChatMsg(CHATBOT_COMMANDS.responses.noSelectedTransaction);
        this.hideLoading();
        return;
      }

      const responses = {
        dataPlanPrepaidTrxState: '[apiresponse] dataPlanPrepaidTrxStateIs<state> [/apiresponse]',
        dataPlanPrepaidTrxStateIsNotProcessedOrSucceededOrRemitted:
          '[apiresponse] dataPlanPrepaidTrxStateIsNotProcessedOrSucceededOrRemitted [/apiresponse]',
        checkDataPlanPrepaidTrxStateIsError: '[apiresponse] checkDataPlanPrepaidTrxStateIsError [/apiresponse]',
        trxStateIsReadyAutorefundForProcessed: '[apiresponse] trxStateIsReadyAutorefundForProcessed [/apiresponse]',
      };

      try {
        // Get invoice data
        const {
          data: [invoiceData],
        } = await getInvoices(this.$axios, { keywords: currentTrx.paymentId });
        const {
          transactions: [invoiceTrx],
        } = invoiceData;

        const { data: phoneCreditPrepaidData } = await getPhoneCreditPrepaidTransaction(this.$axios, invoiceTrx.id);
        const { state } = phoneCreditPrepaidData;

        if (state === 'processed') {
          this.$zopim.sendChatMsg(responses.trxStateIsReadyAutorefundForProcessed);
          return;
        }

        if (['succeeded', 'remitted'].includes(state)) {
          const capitalizedState = state.charAt(0).toUpperCase() + state.slice(1);
          const finalResponse = responses.dataPlanPrepaidTrxState.replace('<state>', capitalizedState);
          this.$zopim.sendChatMsg(finalResponse);
          return;
        }
        this.$zopim.sendChatMsg(responses.dataPlanPrepaidTrxStateIsNotProcessedOrSucceededOrRemitted);
      } catch (error) {
        this.$zopim.sendChatMsg(responses.checkDataPlanPrepaidTrxStateIsError);
        console.error(
          '[ERROR][Autorefund For Processed] Something is wrong when trying to check user transaction state',
          error
        );
      } finally {
        this.hideLoading();
      }
    },

    /**
     * Execute auto refund for processed
     * Support phone credit and data plan
     * @command - [apicall] executeAutorefundForProcessed [/apicall]
     * @param {object} currentTrx - currently selected transaction object
     * errorCodeMapping: https://blueprint.bukalapak.io/t/mitra~bantuan~mitra~bantuan~chatbots~transaction~post~mitra-bantuan~chatbots~transactions~id~refund
     */
    async executeAutorefundForProcessed(currentTrx = null) {
      this.showLoading();
      if (!currentTrx) {
        this.$zopim.sendChatMsg(CHATBOT_COMMANDS.responses.noSelectedTransaction);
        this.hideLoading();
        return;
      }

      const responses = {
        executeAutorefundForProcessedSuccess: '[apiresponse] executeAutorefundForProcessedSuccess [/apiresponse]',
        executeAutorefundFailedForProcessedGeneral: '[apiresponse] executeAutorefundFailedForProcessedGeneral [/apiresponse]',
        executeAutorefundForProcessedFailedTimeNotMeetRequirement: '[apiresponse] executeAutorefundForProcessedFailedTimeNotMeetRequirement [/apiresponse]',
      };

      const errorCodeMapping = {
        FailedTimeNotMeetRequirement: 17101
      };

      try {
        // Get invoice data
        const {
          data: [invoiceData],
        } = await getInvoices(this.$axios, { keywords: currentTrx.paymentId });
        const {
          transactions: [invoiceTrx],
        } = invoiceData;

        const { meta } = await chatbotRefundTransaction(this.$axios, {
          id: invoiceTrx.id,
          requestPayload: {
            type: invoiceTrx.type,
            action: 'refund_processed'
          }
        });

        if (meta.http_status === 200) {
          this.$zopim.sendChatMsg(responses.executeAutorefundForProcessedSuccess);
          return;
        }

        this.$zopim.sendChatMsg(responses.executeAutorefundFailedForProcessedGeneral);
      } catch (error) {
        const status = error.response ? error.response.status : null;
        const errorData = error.response ? error.response.data : null;

        if (status === 422 && errorData && errorData.errors) {
          const errorCode = errorData.errors[0].code;
          if (errorCode === errorCodeMapping.FailedTimeNotMeetRequirement) {
            this.$zopim.sendChatMsg(responses.executeAutorefundForProcessedFailedTimeNotMeetRequirement);
            return;
          }
        }

        this.$zopim.sendChatMsg(responses.executeAutorefundFailedForProcessedGeneral);
        console.error('[ERROR][Autorefund For Processed] Something is wrong when trying to execute autorefund', error);
      } finally {
        this.hideLoading();
      }
    },

    /**
     * Execute auto refund for secondary failed
     * Support phone credit and data plan
     * @command - [apicall] executeAutorefundForSecondaryFailed [/apicall]
     * @param {object} currentTrx - currently selected transaction object
     * errorCodeMapping: https://blueprint.bukalapak.io/t/mitra~bantuan~mitra~bantuan~chatbots~transaction~post~mitra-bantuan~chatbots~transactions~id~refund
     */
    async executeAutorefundForSecondaryFailed(currentTrx = null) {
      this.showLoading();
      if (!currentTrx) {
        this.$zopim.sendChatMsg(CHATBOT_COMMANDS.responses.noSelectedTransaction);
        this.hideLoading();
        return;
      }

      const responses = {
        executeAutorefundForSecondaryFailedSuccess: '[apiresponse] executeAutorefundForSecondaryFailedSuccess [/apiresponse]',
        executeAutorefundForSecondaryFailedErrorGeneral: '[apiresponse] executeAutorefundForSecondaryFailedErrorGeneral [/apiresponse]',
        executeAutorefundForSecondaryFailedErrorPartnerAdviceNotFailed: '[apiresponse] executeAutorefundForSecondaryFailedErrorPartnerAdviceNotFailed [/apiresponse]',
        executeAutorefundForSecondaryFailedErrorBudgetUsageExceeded: '[apiresponse] executeAutorefundForSecondaryFailedErrorBudgetUsageExceeded [/apiresponse]',
      };

      const errorCodeMapping = {
        ErrorPartnerAdviceNotFailed: 75102,
        ErrorBudgetUsageExceeded: 75103
      };

      try {
        // Get invoice data
        const {
          data: [invoiceData],
        } = await getInvoices(this.$axios, { keywords: currentTrx.paymentId });
        const {
          transactions: [invoiceTrx],
        } = invoiceData;

        const { meta } = await chatbotRefundTransaction(this.$axios, {
          id: invoiceTrx.id,
          requestPayload: {
            type: invoiceTrx.type,
            action: 'refund_succeeded'
          }
        });

        if (meta.http_status === 200) {
          this.$zopim.sendChatMsg(responses.executeAutorefundForSecondaryFailedSuccess);
          return;
        }

        this.$zopim.sendChatMsg(responses.executeAutorefundForSecondaryFailedErrorGeneral);
      } catch (error) {
        const status = error.response ? error.response.status : null;
        const errorData = error.response ? error.response.data : null;

        if (status === 422 && errorData && errorData.errors) {
          const errorCode = errorData.errors[0].code;
          switch (errorCode) {
            case errorCodeMapping.ErrorPartnerAdviceNotFailed:
              this.$zopim.sendChatMsg(responses.executeAutorefundForSecondaryFailedErrorPartnerAdviceNotFailed);
              return;
            case errorCodeMapping.ErrorBudgetUsageExceeded:
              this.$zopim.sendChatMsg(responses.executeAutorefundForSecondaryFailedErrorBudgetUsageExceeded);
              return;
          }
        }

        this.$zopim.sendChatMsg(responses.executeAutorefundForSecondaryFailedErrorGeneral);
        console.error('[ERROR][Autorefund For Secondary Failed] Something is wrong when trying to execute autorefund', error);
      } finally {
        this.hideLoading();
      }
    },

    trackVisitAutorefund(msgs) {
      if (msgs.some(msg => VP_DID_NOT_RECEIVE_CHAT_OPTIONS.includes(msg))) {
        this.trackScreenVisit({ current_screen: 'chatbot_option_vp_did_not_receive' });
      };

      if (msgs.some(msg => CUSTOMER_WAITING_STATE_CHAT_OPTIONS.includes(msg))) {
        this.trackScreenVisit({ current_screen: 'chatbot_option_customer_waiting_state' });
      };
    },

    trackClickAutorefund(msg) {
      const urlParams = new URLSearchParams(window.location.search);
      const chat = urlParams.get('chat');

      if (!chat) return;
      
      const payload = base64UrlToJson(chat);

      if (VP_DID_NOT_RECEIVE_CHAT_OPTIONS.includes(msg)) {
        this.trackBantuanEventClick({
          current_screen: 'chatbot_option_vp_did_not_receive',
          click_source: 'click_vp_did_not_receive',
          params: {
            bl_invoice: payload?.paymentId || '',
            product_type: payload?.trxType || '',
          }
        });
      };

      if (CUSTOMER_WAITING_STATE_CHAT_OPTIONS.includes(msg)) {
        const sourceMapping = {
          'Pelanggan masih di warung': 'click_customer_wait',
          'Pelanggan sudah pulang': 'click_customer_left'
        };
        this.trackBantuanEventClick({
          current_screen: 'chatbot_option_customer_waiting_state',
          click_source: sourceMapping[msg],
          params: {
            bl_invoice: payload?.paymentId || '',
            product_type: payload?.trxType || '',
          }
        });
      };
    }
  },
};
